// Sales Orders data layer (Phase 4) — header + line items.
//
// The SINGLE interface between the app and the sales-order schema
// (supabase/migrations/20260630204649_sales_orders.sql).
//
// SECURITY MODEL (server-enforced; mirrored here):
//   • We NEVER send company_id or so_number — DB triggers own them (company
//     forced/inherited; so_number auto = SO-00001; both immutable). RLS scopes all.
//
// INVENTORY INTEGRATION (the defining requirement):
//   • A Sales Order RESERVES stock — it raises reserved_stock and lowers
//     available_stock, but NEVER reduces current_stock (physical stock only moves
//     on dispatch). After every write we call inventory.syncSalesOrder (idempotent),
//     which posts 'reservation' movements capped at available stock (no overselling)
//     and writes the actual reserved amount back to each line. Cancel/fulfil release;
//     delete restores. No drift.
//
// RETURN-TYPE CONTRACT (consistent with purchaseOrders):
//   collection → { data, count, page?, pageSize? } · single → T|null ·
//   create/update → id (string) · delete → void · all THROW a mapped Error.

import { supabase } from "./supabaseClient";
import { syncSalesOrder, removeSalesOrder } from "./inventory";
import { C } from "../customers/theme";

const SO = "sales_orders";
const LINES = "sales_order_lines";

export const DEFAULT_PAGE_SIZE = 25;

/** Colour-coded statuses (+ DB CHECK parity). */
export const SO_STATUSES = [
  { value: "draft", label: "Draft", color: C.textMuted },
  { value: "confirmed", label: "Confirmed", color: C.primary },
  { value: "partially_fulfilled", label: "Partially Fulfilled", color: C.warning },
  { value: "fulfilled", label: "Fulfilled", color: C.success },
  { value: "cancelled", label: "Cancelled", color: C.danger },
];

/** Statuses that hold stock (mirror inventory.SO_RESERVING_STATUSES — used by the UI). */
export const RESERVING_STATUSES = ["draft", "confirmed", "partially_fulfilled"];

export const CURRENCIES = [
  { value: "EUR", label: "EUR (€)" },
  { value: "GBP", label: "GBP (£)" },
  { value: "USD", label: "USD ($)" },
];

export const SO_SORTS = [
  { value: "number", label: "SO No." },
  { value: "order_date", label: "Order Date" },
  { value: "status", label: "Status" },
  { value: "total", label: "Total" },
  { value: "created", label: "Newest" },
];

const HEADER_WRITE_FIELDS = ["customer_id", "customer_name", "order_date", "requested_delivery_date", "salesperson", "reference", "status", "preferred_currency", "notes"];
const LINE_WRITE_FIELDS = ["product_id", "sku", "description", "quantity", "unit_price", "discount_percent", "vat_rate"];
const SORT_COLUMNS = { number: "so_number", order_date: "order_date", status: "status", total: "total_amount", created: "created_at" };

const HEADER_SELECT =
  "id, so_number, customer_id, customer_name, order_date, requested_delivery_date, salesperson, reference, " +
  "status, preferred_currency, subtotal, total_discount, total_vat, total_amount, created_at, updated_at, " +
  "customers ( id, company_name )";
const FULL_SELECT =
  "*, customers ( id, company_name, payment_terms, preferred_currency ), " +
  "sales_order_lines ( id, product_id, sku, description, quantity, reserved_quantity, allocated_quantity, unit_price, discount_percent, vat_rate, line_total, position )";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}
const pick = (obj, fields) => {
  const out = {};
  for (const f of fields) if (obj && obj[f] !== undefined) out[f] = obj[f] === "" ? null : obj[f];
  return out;
};
const sanitizeTerm = (t) => String(t || "").trim().replace(/[%,()]/g, "");
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function toError(error, fallback) {
  const code = error && error.code;
  let message;
  switch (code) {
    case "23514": message = "Invalid value — check the status field."; break;
    case "23503": message = "Referenced customer or product does not exist."; break;
    case "23502": message = "A required field is missing."; break;
    case "PGRST116": message = "Sales order not found, or you do not have access to it."; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

// ── Totals (form preview AND on save) ────────────────────────────────────────

const lineNet = (l) => (Number(l.quantity) || 0) * (Number(l.unit_price) || 0) * (1 - (Number(l.discount_percent) || 0) / 100);

/**
 * Compute per-line and document totals. line_total = net (qty · price · (1 − disc%)); VAT excluded.
 * @param {Array} lines
 * @returns {{ lines: Array, subtotal: number, total_discount: number, total_vat: number, total_amount: number }}
 */
export function computeTotals(lines = []) {
  let subtotal = 0, total_discount = 0, total_vat = 0;
  const computed = lines.map((l, i) => {
    const net = lineNet(l);
    const gross = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
    subtotal += net;
    total_discount += gross - net;
    total_vat += net * ((Number(l.vat_rate) || 0) / 100);
    return { ...l, line_total: round2(net), position: i };
  });
  return {
    lines: computed,
    subtotal: round2(subtotal),
    total_discount: round2(total_discount),
    total_vat: round2(total_vat),
    total_amount: round2(subtotal + total_vat),
  };
}

// ── Reads ────────────────────────────────────────────────────────────────────

/**
 * List sales orders (RLS-scoped) with search, status/customer filters, sort, pagination.
 * @param {Object} [opts]
 * @returns {Promise<{data:Array,count:number,page:number,pageSize:number}>}
 */
export async function getSalesOrders(opts = {}) {
  const { search, status, customerId, page = 1, pageSize = DEFAULT_PAGE_SIZE, sortBy = "created", sortDir = "desc" } = opts;
  const c = client();
  const column = SORT_COLUMNS[sortBy] || "created_at";
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.max(1, Number(pageSize) || DEFAULT_PAGE_SIZE);
  const from = (safePage - 1) * safeSize;
  let q = c.from(SO).select(HEADER_SELECT, { count: "exact" }).order(column, { ascending: sortDir !== "desc" }).range(from, from + safeSize - 1);
  if (status && status !== "all") q = q.eq("status", status);
  if (customerId && customerId !== "all") q = q.eq("customer_id", customerId);
  const term = sanitizeTerm(search);
  if (term) q = q.or(`so_number.ilike.%${term}%,customer_name.ilike.%${term}%,reference.ilike.%${term}%,salesperson.ilike.%${term}%`);
  const { data, error, count } = await q;
  if (error) throw toError(error, "Could not load sales orders");
  return { data: data || [], count: count ?? 0, page: safePage, pageSize: safeSize };
}

export async function searchSalesOrders(term, opts = {}) { return getSalesOrders({ ...opts, search: term }); }

/** Full sales order (header + customer + lines, ordered). */
export async function getSalesOrder(id) {
  const { data, error } = await client().from(SO).select(FULL_SELECT).eq("id", id).maybeSingle();
  if (error) throw toError(error, "Could not load sales order");
  if (data?.sales_order_lines) data.sales_order_lines.sort((a, b) => (a.position || 0) - (b.position || 0));
  return data || null;
}

// ── Writes ───────────────────────────────────────────────────────────────────

async function replaceLines(soId, lines) {
  const c = client();
  const { error: delErr } = await c.from(LINES).delete().eq("sales_order_id", soId);
  if (delErr) throw toError(delErr, "Could not update line items");
  const rows = (lines || [])
    .filter((l) => l.product_id || (l.description && l.description.trim()) || Number(l.quantity) > 0)
    .map((l, i) => ({ ...pick(l, LINE_WRITE_FIELDS), line_total: round2(lineNet(l)), position: i, sales_order_id: soId }));
  if (rows.length) {
    const { error } = await c.from(LINES).insert(rows);
    if (error) throw toError(error, "Could not save line items");
  }
}

/**
 * Create a sales order with its lines, then reserve stock.
 * @param {Object} payload Header fields + `lines: Array`.
 * @returns {Promise<string>} new SO id.
 */
export async function createSalesOrder(payload) {
  const c = client();
  const totals = computeTotals(payload?.lines || []);
  const headerRow = {
    ...pick(payload || {}, HEADER_WRITE_FIELDS),
    subtotal: totals.subtotal, total_discount: totals.total_discount, total_vat: totals.total_vat, total_amount: totals.total_amount,
  };
  const { data, error } = await c.from(SO).insert(headerRow).select("id").single();
  if (error) throw toError(error, "Could not create sales order");
  await replaceLines(data.id, totals.lines);
  await syncSalesOrder(data.id); // reserve stock (capped at available)
  return data.id;
}

/**
 * Update a sales order header + replace its lines, then re-reconcile reservations.
 * @param {string} id @param {Object} payload Header fields + `lines: Array`.
 * @returns {Promise<string>} the SO id.
 */
export async function updateSalesOrder(id, payload) {
  const c = client();
  const totals = computeTotals(payload?.lines || []);
  const headerRow = {
    ...pick(payload || {}, HEADER_WRITE_FIELDS),
    subtotal: totals.subtotal, total_discount: totals.total_discount, total_vat: totals.total_vat, total_amount: totals.total_amount,
  };
  const { error } = await c.from(SO).update(headerRow).eq("id", id).select("id").single();
  if (error) throw toError(error, "Could not update sales order");
  await replaceLines(id, totals.lines);
  await syncSalesOrder(id); // recalculate reservations
  return id;
}

/**
 * Delete a sales order (cascades its lines), then release its reservations.
 * @param {string} id @returns {Promise<void>}
 */
export async function deleteSalesOrder(id) {
  const { error } = await client().from(SO).delete().eq("id", id);
  if (error) throw toError(error, "Could not delete sales order");
  await removeSalesOrder(id); // restore reserved stock
}
